import React, { useState, useEffect } from 'react';
import { accountingAPI, formatCurrency } from '../services/api';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, DollarSign, Download } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import api from '../services/api';
import { formatDateDisplay } from '../lib/dateUtils';

const PIE_COLORS = ['#10b981', '#f59e0b', '#ef4444', '#6366f1', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

export default function ProfitLoss() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ date_from: '', date_to: '' });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const res = await accountingAPI.profitLoss(params);
      setData(res.data);
    } catch { toast.error('Failed to load P&L'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filters.date_from, filters.date_to]);

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>;

  const revenueChart = data?.revenue?.items?.map(i => ({ name: i.account_name.replace('Freight Income - ', ''), value: i.amount })) || [];
  const directExpChart = data?.direct_expenses?.items?.map(i => ({ name: i.account_name, value: i.amount })) || [];
  const indirectExpChart = data?.indirect_expenses?.items?.map(i => ({ name: i.account_name, value: i.amount })) || [];
  const summaryChart = [
    { name: 'Revenue', value: data?.revenue?.total || 0 },
    { name: 'Direct Exp', value: data?.direct_expenses?.total || 0 },
    { name: 'Indirect Exp', value: data?.indirect_expenses?.total || 0 },
    { name: 'Net Profit', value: data?.net_profit || 0 },
  ];

  return (
    <div data-testid="profit-loss-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900">Profit & Loss Statement</h1>
          <p className="text-sm text-slate-500 mt-1">Period: {formatDateDisplay(data?.period?.from)} to {formatDateDisplay(data?.period?.to)}</p>
        </div>
        <div className="flex gap-3">
          <input type="date" value={filters.date_from} onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))}
            className="form-input w-36" data-testid="pnl-date-from" />
          <input type="date" value={filters.date_to} onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))}
            className="form-input w-36" data-testid="pnl-date-to" />
          <button onClick={async () => {
            try {
              const params = new URLSearchParams({ format: 'csv' });
              if (filters.date_from) params.set('date_from', filters.date_from);
              if (filters.date_to) params.set('date_to', filters.date_to);
              const res = await api.get(`/accounting/profit-loss/export?${params}`, { responseType: 'blob' });
              const url = URL.createObjectURL(new Blob([res.data]));
              const a = document.createElement('a'); a.href = url; a.download = 'profit_loss.csv'; a.click();
              toast.success('P&L exported');
            } catch { toast.error('Export failed'); }
          }} className="btn-accent flex items-center gap-1.5 text-xs px-3" data-testid="pnl-export">
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="pnl-revenue-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider">Total Revenue</p>
              <p className="text-2xl font-bold font-mono mt-1 text-emerald-700">Rs. {formatCurrency(data?.revenue?.total || 0)}</p>
            </div>
            <div className="w-10 h-10 rounded-md bg-emerald-50 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-emerald-600" /></div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="pnl-direct-exp-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] font-semibold text-rose-600 uppercase tracking-wider">Direct Expenses</p>
              <p className="text-2xl font-bold font-mono mt-1 text-rose-700">Rs. {formatCurrency(data?.direct_expenses?.total || 0)}</p>
            </div>
            <div className="w-10 h-10 rounded-md bg-rose-50 flex items-center justify-center"><TrendingDown className="w-5 h-5 text-rose-600" /></div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="pnl-gross-profit-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] font-semibold text-blue-600 uppercase tracking-wider">Gross Profit</p>
              <p className={`text-2xl font-bold font-mono mt-1 ${(data?.gross_profit || 0) >= 0 ? 'text-blue-700' : 'text-rose-700'}`}>
                Rs. {formatCurrency(Math.abs(data?.gross_profit || 0))}
              </p>
            </div>
            <div className="w-10 h-10 rounded-md bg-blue-50 flex items-center justify-center"><DollarSign className="w-5 h-5 text-blue-600" /></div>
          </div>
        </div>
        <div className={`border rounded-md p-5 ${(data?.net_profit || 0) >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`} data-testid="pnl-net-profit-card">
          <p className="text-[10px] font-semibold uppercase tracking-wider">{(data?.net_profit || 0) >= 0 ? 'Net Profit' : 'Net Loss'}</p>
          <p className={`text-2xl font-bold font-mono mt-1 ${(data?.net_profit || 0) >= 0 ? 'text-emerald-800' : 'text-rose-800'}`}>
            Rs. {formatCurrency(Math.abs(data?.net_profit || 0))}
          </p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <h3 className="font-heading font-semibold text-slate-800 mb-4 text-sm">Financial Summary</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={summaryChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => `Rs. ${formatCurrency(v)}`} />
              <Bar dataKey="value" fill="#f97316" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        {revenueChart.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <h3 className="font-heading font-semibold text-slate-800 mb-4 text-sm">Revenue Breakdown</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={revenueChart} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {revenueChart.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => `Rs. ${formatCurrency(v)}`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Detailed P&L */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full" data-testid="pnl-detail-table">
          <thead>
            <tr className="bg-slate-50">
              <th className="px-5 py-3 text-left text-xs font-bold text-slate-500 uppercase">Particulars</th>
              <th className="px-5 py-3 text-right text-xs font-bold text-slate-500 uppercase w-40">Amount (Rs.)</th>
            </tr>
          </thead>
          <tbody>
            <tr className="bg-emerald-50/50"><td className="px-5 py-2 font-semibold text-emerald-800 text-sm" colSpan={2}>Revenue</td></tr>
            {data?.revenue?.items?.map((item, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="px-5 py-2 text-sm text-slate-700 pl-10">{item.account_name}</td>
                <td className="px-5 py-2 text-right font-mono text-sm">{formatCurrency(item.amount)}</td>
              </tr>
            ))}
            <tr className="bg-emerald-50 font-semibold">
              <td className="px-5 py-2 text-sm">Total Revenue</td>
              <td className="px-5 py-2 text-right font-mono text-sm">{formatCurrency(data?.revenue?.total || 0)}</td>
            </tr>

            <tr className="bg-rose-50/50"><td className="px-5 py-2 font-semibold text-rose-800 text-sm" colSpan={2}>Less: Direct Expenses (Cost of Service)</td></tr>
            {data?.direct_expenses?.items?.map((item, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="px-5 py-2 text-sm text-slate-700 pl-10">{item.account_name}</td>
                <td className="px-5 py-2 text-right font-mono text-sm">{formatCurrency(item.amount)}</td>
              </tr>
            ))}
            <tr className="bg-rose-50 font-semibold">
              <td className="px-5 py-2 text-sm">Total Direct Expenses</td>
              <td className="px-5 py-2 text-right font-mono text-sm">{formatCurrency(data?.direct_expenses?.total || 0)}</td>
            </tr>

            <tr className="bg-blue-50 font-bold">
              <td className="px-5 py-3 text-sm text-blue-800">Gross Profit</td>
              <td className="px-5 py-3 text-right font-mono text-sm text-blue-800">{formatCurrency(data?.gross_profit || 0)}</td>
            </tr>

            <tr className="bg-orange-50/50"><td className="px-5 py-2 font-semibold text-orange-800 text-sm" colSpan={2}>Less: Indirect Expenses (Operating)</td></tr>
            {data?.indirect_expenses?.items?.map((item, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="px-5 py-2 text-sm text-slate-700 pl-10">{item.account_name}</td>
                <td className="px-5 py-2 text-right font-mono text-sm">{formatCurrency(item.amount)}</td>
              </tr>
            ))}
            <tr className="bg-orange-50 font-semibold">
              <td className="px-5 py-2 text-sm">Total Indirect Expenses</td>
              <td className="px-5 py-2 text-right font-mono text-sm">{formatCurrency(data?.indirect_expenses?.total || 0)}</td>
            </tr>

            <tr className={`font-bold text-lg ${(data?.net_profit || 0) >= 0 ? 'bg-emerald-100' : 'bg-rose-100'}`}>
              <td className="px-5 py-4">{(data?.net_profit || 0) >= 0 ? 'Net Profit' : 'Net Loss'}</td>
              <td className="px-5 py-4 text-right font-mono">{formatCurrency(Math.abs(data?.net_profit || 0))}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
